// ============================================================
// TYPES / MODELS - Mock data contracts
// Ready to map to real backend endpoints when connected
// ============================================================

/**
 * @typedef {Object} CategoryDto
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {boolean} isActive
 * @property {number} productCount
 */

/**
 * @typedef {Object} ProductCardDto
 * @property {string} id
 * @property {string} name
 * @property {string} shortDescription
 * @property {number} price
 * @property {string} unit
 * @property {boolean} isAvailable
 * @property {boolean} featured
 * @property {string} categoryId
 * @property {string} categoryName
 * @property {string} primaryImageUrl
 */

/**
 * @typedef {Object} ProductDetailsDto
 * @property {string} id
 * @property {string} name
 * @property {string} shortDescription
 * @property {string} description
 * @property {number} price
 * @property {string} unit
 * @property {boolean} isAvailable
 * @property {boolean} featured
 * @property {string} categoryId
 * @property {string} categoryName
 * @property {string} primaryImageUrl
 * @property {string[]} images
 */

/**
 * @typedef {Object} HomePageDto
 * @property {string} heroTitle
 * @property {string} heroSubtitle
 * @property {ProductCardDto[]} featuredProducts
 * @property {CategoryDto[]} categories
 */

/**
 * @typedef {Object} CartItem
 * @property {ProductCardDto} product
 * @property {number} quantity
 */

/**
 * @typedef {Object} CreateOrderRequest
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} phone
 * @property {string} email
 * @property {string} address
 * @property {string} city
 * @property {string} postalCode
 * @property {string} note
 * @property {Array<{productId: string, quantity: number}>} items
 */

/**
 * @typedef {Object} OrderPreviewResponse
 * @property {Array<{productId: string, name: string, price: number, quantity: number, total: number}>} items
 * @property {number} subtotal
 * @property {number} shipping
 * @property {number} total
 */

/**
 * @typedef {'NEW'|'REJECTED'|'SHIPPED'|'DELIVERED'|'NOT_PICKED_UP'} OrderStatus
 */

/**
 * @typedef {Object} AdminOrderListItemDto
 * @property {string} id
 * @property {string} orderNumber
 * @property {string} customerName
 * @property {string} phone
 * @property {string} email
 * @property {OrderStatus} status
 * @property {number} total
 * @property {string} createdAt
 * @property {boolean} isReturningCustomer
 * @property {number} previousOrdersCount
 */

/**
 * @typedef {Object} AdminOrderDetailsDto
 * @property {string} id
 * @property {string} orderNumber
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} phone
 * @property {string} email
 * @property {string} address
 * @property {string} city
 * @property {string} postalCode
 * @property {string} note
 * @property {OrderStatus} status
 * @property {Array} items
 * @property {number} subtotal
 * @property {number} shipping
 * @property {number} total
 * @property {string} createdAt
 * @property {boolean} isReturningCustomer
 * @property {number} previousOrdersCount
 * @property {string|null} lastOrderDate
 */

/**
 * @typedef {Object} AdminDashboardDto
 * @property {number} newOrders
 * @property {number} shippedOrders
 * @property {number} deliveredOrders
 * @property {number} rejectedOrders
 * @property {number} totalProducts
 * @property {Array<{productId: string, productName: string, soldQuantity: number, orderCount: number}>} topProducts
 * @property {AdminOrderListItemDto[]} recentOrders
 */

export const ORDER_STATUS = {
  NEW: 'NEW',
  REJECTED: 'REJECTED',
  SHIPPED: 'SHIPPED',
  DELIVERED: 'DELIVERED',
  NOT_PICKED_UP: 'NOT_PICKED_UP'
};

export const ORDER_STATUS_LABELS = {
  NEW: 'Nova',
  REJECTED: 'Odbijena',
  SHIPPED: 'Poslata',
  DELIVERED: 'Preuzeta',
  NOT_PICKED_UP: 'Nije preuzeta'
};

export const ORDER_STATUS_COLORS = {
  NEW: 'bg-blue-50 text-blue-700 border-blue-200',
  REJECTED: 'bg-red-50 text-red-700 border-red-200',
  SHIPPED: 'bg-amber-50 text-amber-700 border-amber-200',
  DELIVERED: 'bg-green-50 text-green-700 border-green-200',
  NOT_PICKED_UP: 'bg-orange-50 text-orange-700 border-orange-200'
};
